import { z } from 'zod';
/** Date ISO acceptée aux frontières qui ne manipulent pas un Timestamp Firestore. */
export const isoDateSchema = z.string().datetime();
/**
 * Forme structurelle d’un Timestamp Firestore. Le schéma reste volontairement
 * indépendant des SDK Firebase pour pouvoir être utilisé dans le navigateur comme dans Functions.
 */
export const firestoreTimestampLikeSchema = z
    .object({
    seconds: z.number().int(),
    nanoseconds: z.number().int().min(0).max(999999999),
})
    // Les Timestamp du SDK admin exposent `seconds` et `nanoseconds` via des
    // getters, tout en portant `_seconds` et `_nanoseconds` comme clés propres.
    // Ces détails d'implémentation ne doivent pas invalider une instance réelle.
    .passthrough();
/**
 * Date réellement rencontrée dans un shipment persisté : ISO à la création,
 * puis souvent Timestamp après une écriture avec `serverTimestamp()`.
 */
export const persistedTimestampSchema = z.union([
    isoDateSchema,
    z.date(),
    firestoreTimestampLikeSchema,
]);
/**
 * Compatibilité legacy en lecture uniquement.
 *
 * Accepte les propriétés privées `_seconds`/`_nanoseconds` exposées par
 * d'anciennes sérialisations Firestore ainsi que les objets SDK dotés de
 * `toDate()`. Les nouvelles écritures doivent utiliser `persistedTimestampSchema`.
 */
export const persistedTimestampLegacyReadSchema = z.union([
    persistedTimestampSchema,
    z.object({ _seconds: z.number().int(), _nanoseconds: z.number().int().optional() }).passthrough(),
    z.custom((value) => typeof value === 'object' &&
        value !== null &&
        typeof value.toDate === 'function'),
]);
export const shipmentTimestampsSchema = z
    .object({ createdAt: persistedTimestampSchema, updatedAt: persistedTimestampSchema })
    .strict();
/**
 * Normalise une date persistée vers le contrat portable canonique.
 *
 * Les branches `_seconds` et `toDate()` existent exclusivement pour relire les
 * documents legacy ; elles ne doivent pas servir de format d'écriture.
 */
export function normalizePersistedTimestamp(value) {
    const canonical = persistedTimestampSchema.safeParse(value);
    if (canonical.success)
        return canonical.data;
    if (value && typeof value === 'object') {
        const legacy = value;
        if (typeof legacy.toDate === 'function') {
            try {
                const date = legacy.toDate();
                return Number.isNaN(date.getTime()) ? null : date;
            }
            catch {
                return null;
            }
        }
        if (Number.isInteger(legacy._seconds)) {
            return {
                seconds: Number(legacy._seconds),
                nanoseconds: Number.isInteger(legacy._nanoseconds) ? Number(legacy._nanoseconds) : 0,
            };
        }
    }
    return null;
}
/**
 * Convertit une date portable en `Date`, en tolérant les formes Firestore
 * legacy prises en charge par `normalizePersistedTimestamp`.
 */
export function persistedTimestampToDate(value) {
    const normalized = normalizePersistedTimestamp(value);
    if (normalized instanceof Date)
        return Number.isNaN(normalized.getTime()) ? null : new Date(normalized);
    if (typeof normalized === 'string') {
        const date = new Date(normalized);
        return Number.isNaN(date.getTime()) ? null : date;
    }
    return normalized
        ? new Date(normalized.seconds * 1000 + normalized.nanoseconds / 1000000)
        : null;
}
